import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, RefreshCcw, Trash2, MailOpen, ArrowLeft } from "lucide-react";
import { adminApi } from "../lib/api";
import { toast } from "sonner";
import AdminCalendar from "../components/AdminCalendar";

const STATUS_OPTIONS = ["pending", "confirmed", "completed", "cancelled"];
const STORAGE_KEY = "bh_admin_pwd";

export default function AdminPage() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("calendar");
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      verify(saved);
    }
  }, []);

  const verify = async (pwd) => {
    try {
      await adminApi(pwd).post("/admin/verify");
      setPassword(pwd);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, pwd);
      loadData(pwd);
    } catch {
      toast.error("Invalid password");
      sessionStorage.removeItem(STORAGE_KEY);
    }
  };

  const loadData = async (pwd = password) => {
    setLoading(true);
    try {
      const [b, m, p] = await Promise.all([
        adminApi(pwd).get("/bookings"),
        adminApi(pwd).get("/contact"),
        adminApi(pwd).get("/admin/packs"),
      ]);
      setBookings(b.data);
      setMessages(m.data);
      setPacks(p.data);
    } catch {
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await adminApi(password).patch(`/bookings/${id}`, { status });
      setBookings((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status } : b))
      );
      toast.success(`Booking ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const removeBooking = async (id) => {
    if (!confirm("Delete this booking?")) return;
    try {
      await adminApi(password).delete(`/bookings/${id}`);
      setBookings((prev) => prev.filter((b) => b.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setPassword("");
    setBookings([]);
    setMessages([]);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-4">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            verify(password);
          }}
          data-testid="admin-login-form"
          className="w-full max-w-md bg-[#070B14] border border-white/5 p-8 relative"
        >
          <Link
            to="/"
            data-testid="admin-back-home"
            className="absolute -top-10 left-0 inline-flex items-center gap-2 text-xs tracking-widest uppercase text-white/60 hover:text-white"
          >
            <ArrowLeft size={14} /> Back to Home
          </Link>
          <Link
            to="/"
            className="flex items-center gap-3"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_elite-training-hub-29/artifacts/pe71jees_5CB65146-0216-4877-B45D-E702230BD224.png"
              alt="BlueprintHoopz"
              className="h-10 w-auto"
            />
            <span className="font-display text-2xl font-bold uppercase tracking-tight">
              Blueprint<span className="text-[#2563EB]">Hoopz</span>
            </span>
          </Link>
          <h1 className="font-display mt-8 text-3xl font-bold uppercase tracking-tight">
            Admin Access
          </h1>
          <p className="mt-2 text-sm text-[#A1A1AA]">
            Enter the admin password to view bookings.
          </p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            placeholder="Password"
            className="bh-input mt-6"
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            className="mt-4 w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold tracking-widest uppercase text-sm px-6 py-4 rounded-sm transition-colors"
          >
            Sign In
          </button>
        </form>
        <style>{`
          .bh-input { width:100%; background:#050505; border:1px solid rgba(255,255,255,0.08); color:#fff; padding:14px 16px; font-size:14px; outline:none; border-radius:2px; }
          .bh-input:focus { border-color:#2563EB; }
        `}</style>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/5 bg-[#050505]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="font-display text-2xl font-bold uppercase tracking-tight"
          >
            Blueprint<span className="text-[#2563EB]">Hoopz</span>{" "}
            <span className="ml-2 text-xs tracking-widest text-white/40">
              ADMIN
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadData()}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#2563EB] px-4 py-2 text-xs tracking-widest uppercase"
            >
              <RefreshCcw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-[#2563EB] px-4 py-2 text-xs tracking-widest uppercase"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center gap-2 border-b border-white/5">
          {[
            { key: "calendar", label: "Calendar" },
            { key: "bookings", label: `Bookings (${bookings.length})` },
            { key: "packs", label: `Packs (${packs.length})` },
            { key: "messages", label: `Messages (${messages.length})` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-3 text-xs tracking-widest uppercase border-b-2 transition-colors ${
                tab === t.key
                  ? "border-[#2563EB] text-white"
                  : "border-transparent text-white/50 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading && (
          <div className="mt-8 text-sm text-[#A1A1AA]">Loading…</div>
        )}

        {tab === "calendar" && (
          <AdminCalendar bookings={bookings} onStatusChange={updateStatus} />
        )}


        {tab === "bookings" && (
          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.25em] uppercase text-white/50 border-b border-white/5">
                  <th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-4">Slot</th>
                  <th className="py-3 pr-4">Athlete</th>
                  <th className="py-3 pr-4">Contact</th>
                  <th className="py-3 pr-4">Program</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Notes</th>
                  <th className="py-3 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && !loading && (
                  <tr>
                    <td colSpan={8} className="py-8 text-[#A1A1AA] text-center">
                      No bookings yet.
                    </td>
                  </tr>
                )}
                {bookings.map((b) => (
                  <tr
                    key={b.id}
                    data-testid={`booking-row-${b.id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                  >
                    <td className="py-4 pr-4 text-white">{b.date}</td>
                    <td className="py-4 pr-4 text-[#60A5FA]">{b.time_slot}</td>
                    <td className="py-4 pr-4">{b.name}</td>
                    <td className="py-4 pr-4 text-[#A1A1AA]">
                      <div>{b.email}</div>
                      <div>{b.phone}</div>
                    </td>
                    <td className="py-4 pr-4">{b.program}</td>
                    <td className="py-4 pr-4">
                      <select
                        value={b.status}
                        onChange={(e) => updateStatus(b.id, e.target.value)}
                        data-testid={`booking-status-${b.id}`}
                        className="bg-[#050505] border border-white/10 px-2 py-1 text-xs uppercase tracking-wider"
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-4 pr-4 text-[#A1A1AA] max-w-[200px] truncate">
                      {b.notes || "—"}
                    </td>
                    <td className="py-4 pr-4">
                      <button
                        onClick={() => removeBooking(b.id)}
                        data-testid={`booking-delete-${b.id}`}
                        className="text-white/50 hover:text-red-400"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "packs" && (
          <div className="mt-8 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.25em] uppercase text-white/50 border-b border-white/5">
                  <th className="py-3 pr-4">Purchased</th>
                  <th className="py-3 pr-4">Athlete</th>
                  <th className="py-3 pr-4">Email</th>
                  <th className="py-3 pr-4">Pack</th>
                  <th className="py-3 pr-4">Used / Total</th>
                  <th className="py-3 pr-4">Amount</th>
                </tr>
              </thead>
              <tbody>
                {packs.length === 0 && !loading && (
                  <tr>
                    <td colSpan={6} className="py-8 text-[#A1A1AA] text-center">
                      No session packs purchased yet.
                    </td>
                  </tr>
                )}
                {packs.map((p) => (
                  <tr
                    key={p.id}
                    data-testid={`pack-row-${p.id}`}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                  >
                    <td className="py-4 pr-4 text-white/80">
                      {new Date(p.created_at).toLocaleString()}
                    </td>
                    <td className="py-4 pr-4">{p.name}</td>
                    <td className="py-4 pr-4 text-[#A1A1AA]">{p.email}</td>
                    <td className="py-4 pr-4">{p.program}</td>
                    <td className="py-4 pr-4 text-[#60A5FA]">
                      {p.sessions_used} / {p.sessions_total}
                    </td>
                    <td className="py-4 pr-4">${p.amount?.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}


        {tab === "messages" && (
          <div className="mt-8 space-y-3">
            {messages.length === 0 && !loading && (
              <div className="py-8 text-[#A1A1AA] text-center">
                No messages yet.
              </div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`message-row-${m.id}`}
                className="bg-[#070B14] border border-white/5 p-5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#60A5FA]">
                    <MailOpen size={14} /> {m.name}
                  </div>
                  <div className="text-[10px] tracking-widest uppercase text-white/40">
                    {new Date(m.created_at).toLocaleString()}
                  </div>
                </div>
                <div className="mt-2 text-sm text-[#A1A1AA]">
                  {m.email} {m.phone && `· ${m.phone}`}
                </div>
                <div className="mt-3 text-white whitespace-pre-line">
                  {m.message}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
